/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'clsx';
import styles from './SearchInput.module.scss';
import Asset from '../Asset';
import ElementLink from '../ElementLink';
import { coveoQuerySuggest } from '../../apis/coveo';
import { useRouter } from 'next/router';

export const SearchInputPropTypes = {
  size: PropTypes.oneOf(['sm', 'lg']),
  placeholder: PropTypes.string,
  onSearch: PropTypes.func,
  actionCause: PropTypes.string
};

export const SearchInputUIPropTypes = {
  ...SearchInputPropTypes,
  setValue: PropTypes.func,
  value: PropTypes.string
};

const processSuggestions = (userInput, suggestions) => {
  const cleaned = suggestions.map((s) => {
    return s.expression.toLowerCase().replaceAll(/\?/g, '');
  });
  const distinct = new Set(cleaned);

  return [userInput, ...distinct];
};

// TODO: right now we're doing an actual search because we need some search data in order to bootstrap the
// Coveo ML model; change this to a request for suggestions later.
const getSearchSuggestions = async (value) => {
  const rawResponse = await coveoQuerySuggest(value);
  const response = await rawResponse.json();
  return processSuggestions(value, response.completions || []);
};

export const SearchSuggestionPropTypes = {
  children: PropTypes.element.isRequired,
  suggestion: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  onSelected: PropTypes.func.isRequired
};
const SearchSuggestion = ({ children, suggestion, active, onSelected, router }) => {
  return (
    <a
      href={`/search?q=${suggestion}`}
      className={cx({ [styles.searchSuggestion]: true, [styles.activeSuggestion]: active })}
      onClick={(e) => {
        e.preventDefault();
        onSelected();
        router.push(`/search?q=${suggestion}`)
        return false;
      }}>
      {children}
    </a>
  );
};

SearchSuggestion.propTypes = SearchSuggestionPropTypes;

export function SearchInputUI({ size, value, setValue, placeholder, onSearch, query, showSearchIcon, actionCause, parent, setShowUserName }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(0);
  const userEntered = suggestions && suggestions[0];
  const router = useRouter();
  const [showInputField, setShowInputField] = useState(false)
  const [smallScreen, setSmallScreen] = useState()
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setValue(value);

    if (!value.trim()) {
      router.query.q = ""
      // onSearch("");

      router.push(router.pathname, undefined, { shallow: true });
      return;
    }
    if (value.length > 2) {
      getSearchSuggestions(value).then((s) => {
        setSuggestions(s);
      });
    } else {
      setSuggestions([]);
    }

  };

  const handleSearchSubmit = () => {
    const searchValue = value?.trim();
    const params = new URLSearchParams();

    if (searchValue) {
      params.set('q', searchValue);
    }
    if (actionCause) {
      params.set('actionCause', actionCause);
    }

    const queryString = params.toString();
    onSearch(value);
    router.push(queryString ? `/search?${queryString}` : '/search');

  };

  useEffect(() => {
    // When the selected suggestion changes, set the current value to the suggestion
    if (suggestions && suggestions.length && suggestions[selectedSuggestion]) {
      setValue(() => suggestions[selectedSuggestion]);
    }
  }, [selectedSuggestion]);

  const parentRef = useRef(null);
  const iconRef = useRef(null)
  useEffect(() => {
    // We need to listen to the global page onclick for blur purposes,
    // because the parent of an absolute child gets onBlur'd when the child is clicked.
    const onPageClick = (e) => {
      const { target } = e;
      if (!parentRef.current.contains(target)) {
        setFocused(false);
        setShowInputField(false);
        parent && setShowUserName(true)

      }
    };
    /* global window */
    window.addEventListener('mousedown', onPageClick);
    return () => window.removeEventListener('mousedown', onPageClick);
  }, [parentRef]);

  useEffect(() => {
    const handleResize = () => {
      setSmallScreen(window.innerWidth <= 320);
    };
    handleResize()
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const buttonStyle = cx({
    [styles.btnSearchSmall]: size === 'sm',
    [styles.btnSearchLarge]: size === 'lg'
  });

  const searchValue = value?.trim();
  const params = new URLSearchParams();
  if (searchValue) {
    params.set('q', searchValue);
  }
  if (actionCause) {
    params.set('actionCause', actionCause);
  }
  const pageurl = params.toString() ? `/search?${params.toString()}` : '/search';
  return (
    <div
      ref={parentRef}
      data-testid="SearchInput"
      className={cx({ [styles.searchInputInnerWrap]: true, [styles.sm]: size === 'sm' })}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      onFocus={() => {
        setFocused(true);
      }}>
      {(showSearchIcon && !showInputField) && <div className={styles.MobileSearchIcon} ref={iconRef} onClick={() => {
        setShowInputField(true);
        smallScreen && setShowUserName(false);
      }}></div>}
      {(!showSearchIcon || showInputField) && <label className={styles.searchInputGroup} htmlFor="header-search-bar">
        <input
          className={cx({
            [styles.searchInputSmall]: size === 'sm',
            [styles.searchInputLarge]: size === 'lg'
          })}
          placeholder="Search"
          autoComplete="off"
          name="search"
          onChange={(e) => handleSearchChange(e)}
          onKeyDown={(event) => {
            switch (event.key) {
              case 'Enter':
                handleSearchSubmit();
                setFocused(false);
                break;
              case 'Down':
              case 'ArrowDown':
                setSelectedSuggestion((prev) => (prev + 1) % suggestions.length);
                break;
              case 'Up':
              case 'ArrowUp':
                setSelectedSuggestion((prev) => (prev + suggestions.length - 1) % suggestions.length);
                break;
              case 'Esc':
              case 'Escape':
                setFocused(false);
                break;
              default:
                // Any other key, assume we've regained focus
                setFocused(true);
                break;
            }
          }}
          value={value}
          type="search"
          id="header-search-bar"
        />
        <span className={styles.searchInputGroupIconWrap}>
          <a
            href="/search"
            onClick={(e) => {
              e.preventDefault();
              setValue('');
              setSuggestions([])
            }}
            tabIndex={value == "" ? -1 : 0}
            className={cx(buttonStyle, styles.middleButton)}>
            <Asset
              url={value.length > 0 ? '/images/assets/cross.svg' : '/images/assets/blank.svg'}
              title={placeholder}
              isInlineSvg
            />
          </a>
          <a
            href={pageurl}
            onClick={(e) => {
              e.preventDefault();
              handleSearchSubmit(e);
              setSuggestions([])
            }}
            className={buttonStyle}
            trackingid={"global_header_search"}
            download={false}>
            <Asset url="/images/assets/SearchIcon.svg" title={placeholder} isInlineSvg />
          </a>
        </span>
      </label>}
      {focused && suggestions && suggestions.length > 1 && (
        <div className={`${styles.searchSuggestionContainer}`}>
          {suggestions.map((s, idx) => {
            // The first suggestion is always what they've typed in
            if (idx === 0) {
              return null;
            }

            const searchParts = s.split(userEntered);
            const highlightedSearch = searchParts.map((text, index) => {
              if (index < searchParts.length - 1) {
                return (
                  <>
                    {text}
                    <b>{userEntered}</b>
                  </>
                );
              }
              return <>{text}</>;
            });

            return (
              <SearchSuggestion
                key={s}
                active={idx === selectedSuggestion}
                suggestion={s}
                currentSearch={value}
                router={router}
                onSelected={() => {
                  setValue(s);
                  onSearch(s);
                  setFocused(false)
                }}>
                {highlightedSearch}
              </SearchSuggestion>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SearchInput(props) {
  const [value, setValue] = useState('');
  return <SearchInputUI {...{ ...props, value, setValue }} />;
}

SearchInput.propTypes = SearchInputPropTypes;

SearchInput.defaultProps = {
  size: 'sm',
  placeholder: '',
  onSearch: () => { },
  actionCause: undefined
};

SearchInputUI.propTypes = SearchInputUIPropTypes;

export default SearchInput;